import React, { useState, useEffect, useCallback } from 'react';
import { Box, CircularProgress, TextField, InputAdornment, TablePagination } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { couponService } from '../services/couponService';
import { campaignService } from '../services/campaignService';
import { Coupon, Campaign } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { Icon } from '../components/participant/Icon';
import { ParticipantTopBar } from '../components/participant/ParticipantTopBar';
import { BottomSheet } from '../components/participant/BottomSheet';
import { participantColors as c, statusVariants, fadeUpKeyframes } from '../theme/participantTheme';

const STATUS_META: Record<string, { label: string; variant: keyof typeof statusVariants; stubBg: string; stubIcon: string }> = {
  ACTIVE: { label: 'No sorteio', variant: 'success', stubBg: c.surfaceTint, stubIcon: c.brandMid },
  USED: { label: 'Já sorteado', variant: 'info', stubBg: c.infoStubBg, stubIcon: c.infoStubIcon },
  INVALID: { label: 'Inválido', variant: 'error', stubBg: c.errorBg, stubIcon: c.error },
};

const MyCoupons: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [numberSearch, setNumberSearch] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const fetchMyCoupons = useCallback(async () => {
    try {
      setLoading(true);
      const data = await couponService.getMyCoupons({
        number: numberSearch || undefined,
        campaignId: campaignFilter || undefined,
        page: page + 1,
        limit: rowsPerPage,
      });
      setCoupons(data.coupons);
      setTotal(data.total);
    } catch (error) {
      // erro silencioso — lista fica vazia, estado de "nenhum cupom" cobre o caso
    } finally {
      setLoading(false);
    }
  }, [numberSearch, campaignFilter, page, rowsPerPage]);

  useEffect(() => {
    fetchMyCoupons();
  }, [fetchMyCoupons]);

  useEffect(() => {
    campaignService
      .getActiveCampaigns()
      .then((data) => setCampaigns(data || []))
      .catch(() => setCampaigns([]));
  }, []);

  const canEditOrDelete = (coupon: Coupon) => coupon.status === 'ACTIVE' && coupon.user?.id === user?.id;

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await couponService.deleteMyCoupon(deleteTarget);
      setDeleteTarget(null);
      fetchMyCoupons();
    } catch (error) {
      setDeleteTarget(null);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: c.gradients.pageBg, fontFamily: c.fontFamily, pb: '48px' }}>
      <style>{fadeUpKeyframes}</style>

      <Box sx={{ maxWidth: 620, mx: 'auto', px: 2, display: 'flex', flexDirection: 'column', gap: '18px' }}>
        <ParticipantTopBar />

        <Box
          sx={{
            background: c.surface,
            borderRadius: `${c.radius.card}px`,
            padding: '20px',
            boxShadow: c.shadows.card,
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <Box
            component="button"
            onClick={() => navigate('/dashboard')}
            title="Voltar"
            sx={{
              width: 52,
              height: 52,
              flexShrink: 0,
              border: `2px solid ${c.border}`,
              borderRadius: `${c.radius.tile}px`,
              background: c.surfaceHover,
              color: c.brandDark,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': { background: '#e7f4e7', borderColor: c.borderHover },
            }}
          >
            <Icon name="arrow_back" size={24} />
          </Box>
          <Box>
            <Box component="div" sx={{ fontSize: 25, fontWeight: 800, color: c.textDarkest }}>
              Meus cupons
            </Box>
            <Box component="div" sx={{ fontSize: 15, color: c.textSecondary, mt: '2px' }}>
              Acompanhe todos os seus cupons cadastrados
            </Box>
          </Box>
        </Box>

        <Box
          component="button"
          onClick={() => navigate('/user/my-coupons/new')}
          sx={{
            width: '100%',
            minHeight: 58,
            border: 'none',
            borderRadius: `${c.radius.button}px`,
            background: c.gradients.primaryButton,
            color: c.onDarkPrimary,
            fontFamily: c.fontFamily,
            fontSize: 17,
            fontWeight: 800,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            boxShadow: c.shadows.primaryButton,
            transition: 'transform 0.15s',
            '&:hover': { transform: 'translateY(-2px)' },
          }}
        >
          <Icon name="add_circle" size={22} />
          Cadastrar novo cupom
        </Box>

        <TextField
          fullWidth
          placeholder="Buscar por número do cupom..."
          value={numberSearch}
          onChange={(e) => {
            setNumberSearch(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon name="search" size={20} color={c.brandIcon} />
              </InputAdornment>
            ),
          }}
          sx={{
            background: c.surface,
            '& .MuiOutlinedInput-root': { borderRadius: '18px', fontFamily: c.fontFamily },
          }}
        />

        {campaigns.length > 0 && (
          <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {[{ id: '', name: 'Todas' }, ...campaigns].map((cmp) => {
              const active = campaignFilter === cmp.id;
              return (
                <Box
                  key={cmp.id || 'all'}
                  component="button"
                  onClick={() => {
                    setCampaignFilter(cmp.id);
                    setPage(0);
                  }}
                  sx={{
                    border: `2px solid ${active ? c.surfaceDark : c.border}`,
                    borderRadius: `${c.radius.pill}px`,
                    background: active ? c.surfaceDark : c.surface,
                    color: active ? c.onDarkPrimary : c.textSecondary,
                    fontFamily: c.fontFamily,
                    fontSize: 15,
                    fontWeight: 800,
                    padding: '10px 18px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {cmp.name}
                </Box>
              );
            })}
          </Box>
        )}

        <Box component="div" sx={{ fontSize: 14, color: c.textTertiary }}>
          {total} {total === 1 ? 'cupom encontrado' : 'cupons encontrados'}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: c.brandMid }} size={48} />
          </Box>
        ) : coupons.length === 0 ? (
          <Box sx={{ background: c.surface, borderRadius: `${c.radius.card}px`, padding: '40px 24px', textAlign: 'center', boxShadow: c.shadows.card }}>
            <Icon name="confirmation_number" size={52} color={c.iconMuted} />
            <Box component="div" sx={{ fontSize: 19, fontWeight: 800, color: c.textSecondary, mt: '12px' }}>
              Nenhum cupom encontrado
            </Box>
            <Box component="div" sx={{ fontSize: 15, color: c.textTertiary, mt: '4px' }}>
              {numberSearch || campaignFilter ? 'Tente ajustar a busca ou o filtro' : 'Cadastre seu primeiro cupom para participar dos sorteios'}
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {coupons.map((coupon, i) => {
              const meta = STATUS_META[coupon.status?.toUpperCase()] || STATUS_META.ACTIVE;
              const variant = statusVariants[meta.variant];
              const editable = canEditOrDelete(coupon);
              return (
                <Box
                  key={coupon.id}
                  sx={{
                    display: 'flex',
                    background: c.surface,
                    borderRadius: `${c.radius.card}px`,
                    boxShadow: c.shadows.listItem,
                    overflow: 'hidden',
                    animation: `participantFadeUp ${0.3 + i * 0.04}s ease both`,
                  }}
                >
                  <Box
                    sx={{
                      width: 86,
                      flexShrink: 0,
                      borderRight: `3px dashed ${c.border}`,
                      background: meta.stubBg,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '12px 8px',
                    }}
                  >
                    <Icon name="confirmation_number" size={26} color={meta.stubIcon} />
                    <Box component="span" sx={{ fontSize: 11, fontWeight: 800, color: meta.stubIcon, letterSpacing: '0.05em' }}>
                      CUPOM
                    </Box>
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                      <Box component="div" sx={{ fontSize: 24, fontWeight: 800, color: c.textDarkest, letterSpacing: '0.05em' }}>
                        #{coupon.number}
                      </Box>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          borderRadius: `${c.radius.pill}px`,
                          padding: '4px 12px',
                          fontSize: 13,
                          fontWeight: 800,
                          background: variant.bg,
                          color: variant.text,
                        }}
                      >
                        {meta.label}
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Icon name="emoji_events" size={16} color={c.textTertiary} />
                        <Box component="span" sx={{ fontSize: 14, color: c.textSecondary }}>
                          {coupon.campaign?.name || 'Campanha não informada'}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Icon name="storefront" size={16} color={c.textTertiary} />
                        <Box component="span" sx={{ fontSize: 14, color: c.textSecondary }}>
                          {coupon.store?.name || 'Loja não informada'}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Icon name="calendar_month" size={16} color={c.textTertiary} />
                        <Box component="span" sx={{ fontSize: 14, color: c.textSecondary }}>
                          {new Date(coupon.createdAt).toLocaleDateString('pt-BR')}
                        </Box>
                      </Box>
                    </Box>

                    {editable && (
                      <Box sx={{ display: 'flex', gap: '8px', mt: '4px' }}>
                        <Box
                          component="button"
                          onClick={() => navigate(`/user/my-coupons/${coupon.id}/edit`)}
                          sx={{
                            border: `2px solid ${c.borderHover}`,
                            borderRadius: '12px',
                            background: c.surface,
                            color: c.brandDark,
                            fontFamily: c.fontFamily,
                            fontSize: 14,
                            fontWeight: 800,
                            padding: '8px 14px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            '&:hover': { background: c.surfaceHover },
                          }}
                        >
                          <Icon name="edit" size={16} />
                          Editar
                        </Box>
                        <Box
                          component="button"
                          onClick={() => setDeleteTarget(coupon.id)}
                          sx={{
                            border: `2px solid ${c.errorBorderSoft}`,
                            borderRadius: '12px',
                            background: c.surface,
                            color: c.error,
                            fontFamily: c.fontFamily,
                            fontSize: 14,
                            fontWeight: 800,
                            padding: '8px 14px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            '&:hover': { background: c.errorBg },
                          }}
                        >
                          <Icon name="delete" size={16} />
                          Excluir
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}

            <Box sx={{ background: c.surface, borderRadius: `${c.radius.card}px`, boxShadow: c.shadows.card }}>
              <TablePagination
                rowsPerPageOptions={[6, 12, 24]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                labelRowsPerPage="Por página"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                sx={{ fontFamily: c.fontFamily }}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Confirmação de exclusão */}
      <BottomSheet open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
        <Box sx={{ padding: '28px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', textAlign: 'center' }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              background: c.errorBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="delete" size={36} color={c.error} />
          </Box>
          <Box component="div" sx={{ fontSize: 19, fontWeight: 800, color: c.textDarkest }}>
            Excluir este cupom?
          </Box>
          <Box component="div" sx={{ fontSize: 15, color: c.textSecondary }}>
            Essa ação não pode ser desfeita.
          </Box>
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '10px', mt: '8px' }}>
            <Box
              component="button"
              onClick={handleDeleteConfirm}
              sx={{
                width: '100%',
                minHeight: 54,
                border: 'none',
                borderRadius: `${c.radius.button}px`,
                background: c.error,
                color: c.onDarkPrimary,
                fontFamily: c.fontFamily,
                fontSize: 16,
                fontWeight: 800,
                cursor: 'pointer',
                '&:hover': { background: c.errorHover },
              }}
            >
              Excluir cupom
            </Box>
            <Box
              component="button"
              onClick={() => setDeleteTarget(null)}
              sx={{
                width: '100%',
                minHeight: 54,
                border: `2px solid ${c.borderHover}`,
                borderRadius: `${c.radius.button}px`,
                background: c.surface,
                color: c.brandDark,
                fontFamily: c.fontFamily,
                fontSize: 16,
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </Box>
          </Box>
        </Box>
      </BottomSheet>
    </Box>
  );
};

export default MyCoupons;
